import { useEffect, useRef, useState, type MutableRefObject, type PointerEvent } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import {
  BALL_R,
  PADDLE_H,
  PADDLE_INSET,
  PADDLE_W,
  TABLE_H,
  TABLE_W,
  WIN_BY,
  WIN_SCORE,
  createPongState,
  extrapolateSnap,
  paddleBounds,
  stepPong,
  toSnapshot,
  type PongSnapshot,
  type PongState,
} from '@/features/digitalOffice/pong';
import type { PongSession } from '@/features/digitalOffice/usePongSession';

/**
 * The invite that appears when someone walks up to the ping pong table,
 * plus the match itself.
 */

function PaddleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <ellipse cx="13" cy="16" rx="8" ry="9" fill="#1F7A45" stroke="#F4F1EA" strokeWidth="1.4" />
      <rect x="11" y="23" width="4" height="6" rx="1.2" fill="#C4A574" />
      <circle cx="24" cy="9" r="3.1" fill="#F7F5EF" stroke="#1B2E1D" strokeWidth="0.6" />
    </svg>
  );
}

function namesPhrase(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function PingPongLayer({
  session,
  connected,
  enabled = true,
}: {
  session: PongSession;
  connected: boolean;
  /** Hide the walk-up invite while the building editor is open. */
  enabled?: boolean;
}) {
  const showPrompt = enabled && session.phase === 'idle' && !!session.near;
  const showWait = enabled && session.phase === 'waiting';
  const dialogOpen = session.blocking;
  const playing =
    session.link !== 'lost' && (session.phase === 'single' || session.link === 'live');

  return (
    <>
      {showPrompt && (
        <div className="absolute bottom-16 left-1/2 z-20 w-[min(24rem,calc(100%-1.5rem))] -translate-x-1/2 duration-200 animate-in fade-in slide-in-from-bottom-2">
          <div
            role="dialog"
            aria-label="Play ping pong?"
            className="rounded-xl border border-border bg-card/95 p-3 text-card-foreground shadow-lg backdrop-blur"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#1F7A45] text-white shadow-sm">
                <PaddleIcon className="h-7 w-7" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Ping pong</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Play a game? One player is against the computer. Two players waits here until a
                  coworker walks up and chooses the same thing.
                </p>
                {session.waitingHere.length > 0 && (
                  <p className="mt-1.5 text-xs font-medium text-foreground">
                    {namesPhrase(session.waitingHere)}{' '}
                    {session.waitingHere.length === 1 ? 'is' : 'are'} waiting for a partner.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={session.startSingle}>
                One player
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={session.startDual}
                disabled={!connected}
                title={
                  connected
                    ? 'Wait for a coworker at this table'
                    : 'Two players needs a live connection'
                }
              >
                Two players
              </Button>
            </div>
          </div>
        </div>
      )}

      {showWait && (
        <div className="absolute bottom-16 left-1/2 z-20 w-[min(24rem,calc(100%-1.5rem))] -translate-x-1/2 duration-200 animate-in fade-in slide-in-from-bottom-2">
          <div
            role="status"
            className="rounded-xl border border-border bg-card/95 p-3 text-card-foreground shadow-lg backdrop-blur"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#1F7A45]">
                <PaddleIcon className="h-7 w-7 animate-pulse" />
              </span>
              <div>
                <p className="text-sm font-medium">Waiting for a coworker</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {session.waitingHere.length > 0
                    ? `${namesPhrase(session.waitingHere)} ${session.waitingHere.length === 1 ? 'is' : 'are'} at the table. The match starts when they choose Two players.`
                    : 'Stay by the table. The match starts when someone else walks up and chooses Two players.'}
                </p>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button type="button" size="sm" variant="outline" onClick={session.close}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) session.close();
        }}
      >
        <DialogContent
          className="max-h-[calc(100dvh-2rem)] w-[calc(100%-1.5rem)] max-w-3xl overflow-y-auto"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          {session.link === 'connecting' && (
            <Connecting name={session.opponentName ?? 'your coworker'} onCancel={session.close} />
          )}
          {session.link === 'lost' && (
            <Lost
              name={session.opponentName ?? 'Your coworker'}
              reason={session.lostReason}
              onSolo={session.playSoloInstead}
              onWait={session.keepWaiting}
              onClose={session.close}
            />
          )}
          {playing && (
            <PongMatch
              key={session.phase === 'guest' ? 'guest' : session.phase === 'host' ? 'host' : 'solo'}
              mode={
                session.phase === 'guest' ? 'guest' : session.phase === 'host' ? 'host' : 'solo'
              }
              leftName={session.leftName}
              rightName={session.rightName}
              youSide={session.youSide}
              guestYRef={session.guestYRef}
              snapRef={session.snapRef}
              onPublish={session.publish}
              onPaddle={session.sendPaddle}
              onRematch={session.requestRematch}
              rematchNonce={session.rematchNonce}
              onClose={session.close}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Connecting({ name, onCancel }: { name: string; onCancel: () => void }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Starting the match</DialogTitle>
        <DialogDescription>Waiting for {name} to join the table.</DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-3 rounded-lg bg-secondary px-3 py-4">
        <PaddleIcon className="h-8 w-8 animate-pulse" />
        <p className="text-sm text-muted-foreground">You’ll play as soon as {name} is connected.</p>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
}

function Lost({
  name,
  reason,
  onSolo,
  onWait,
  onClose,
}: {
  name: string;
  reason: PongSession['lostReason'];
  onSolo: () => void;
  onWait: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Match ended</DialogTitle>
        <DialogDescription>
          {reason === 'timeout'
            ? `Couldn’t reach ${name}. They may have walked off.`
            : `${name} left the table.`}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Leave
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onWait}>
          Keep waiting
        </Button>
        <Button type="button" size="sm" onClick={onSolo}>
          Play the computer
        </Button>
      </div>
    </>
  );
}

interface Hud {
  scoreL: number;
  scoreR: number;
  phase: PongState['phase'];
  server: PongState['server'];
  winner: PongState['winner'];
  paused: boolean;
}

interface PongMatchProps {
  mode: 'solo' | 'host' | 'guest';
  leftName: string;
  rightName: string;
  youSide: 'left' | 'right';
  guestYRef: MutableRefObject<number | null>;
  snapRef: MutableRefObject<PongSnapshot | null>;
  onPublish: (snap: PongSnapshot) => void;
  onPaddle: (y: number) => void;
  onRematch: () => void;
  rematchNonce: number;
  onClose: () => void;
}

function PongMatch({
  mode,
  leftName,
  rightName,
  youSide,
  guestYRef,
  snapRef,
  onPublish,
  onPaddle,
  onRematch,
  rematchNonce,
  onClose,
}: PongMatchProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hud, setHud] = useState<Hud>({
    scoreL: 0,
    scoreR: 0,
    phase: 'serve',
    server: 'left',
    winner: null,
    paused: false,
  });
  const [muted, setMuted] = useState(false);
  const [askedRematch, setAskedRematch] = useState(false);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const pausedRef = useRef(false);
  const holdRef = useRef({ up: false, down: false });
  const keysRef = useRef({ up: false, down: false });
  const pointerRef = useRef({ active: false, y: TABLE_H / 2 });
  const resetRef = useRef<() => void>(() => {});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const authoritative = mode !== 'guest';
    const keys = keysRef.current;
    const state = createPongState();
    let seq = 0;
    let hitFlash = 0;
    let lastSound = '';
    let lastHud = '';
    let lastPublish = 0;
    let lastPaddleSend = 0;
    let localY = TABLE_H / 2;
    let raf = 0;
    let last = performance.now();

    const resize = () => {
      const cssW = canvas.clientWidth || 640;
      const cssH = cssW * (TABLE_H / TABLE_W);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.floor(cssW * dpr));
      canvas.height = Math.max(1, Math.floor(cssH * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const syncHud = (next: Hud) => {
      const key = `${next.scoreL}|${next.scoreR}|${next.phase}|${next.server}|${next.winner}|${next.paused}`;
      if (key === lastHud) return;
      lastHud = key;
      setHud(next);
      if (next.phase !== 'over') setAskedRematch(false);
    };

    const reset = () => {
      const fresh = createPongState();
      Object.assign(state, fresh);
      hitFlash = 0;
      pausedRef.current = false;
      syncHud({
        scoreL: 0,
        scoreR: 0,
        phase: 'serve',
        server: 'left',
        winner: null,
        paused: false,
      });
    };
    resetRef.current = reset;

    const desiredY = (current: number): number => {
      if (pointerRef.current.active) return pointerRef.current.y;
      let dir = 0;
      if (keys.up || holdRef.current.up) dir -= 1;
      if (keys.down || holdRef.current.down) dir += 1;
      if (dir === 0) return current;
      return current + dir * 4000;
    };

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      let view: PongState;

      if (authoritative) {
        if (!pausedRef.current) {
          const mine = youSide === 'left' ? state.leftY : state.rightY;
          localY = desiredY(mine);
          const result = stepPong(state, dt, {
            leftTarget: youSide === 'left' ? localY : null,
            rightTarget: mode === 'host' ? (guestYRef.current ?? TABLE_H / 2) : null,
            aiRight: mode === 'solo',
          });
          if (result.hit) {
            hitFlash = 1;
            blip('hit', mutedRef.current);
          }
          if (result.scored) blip(result.over ? 'win' : 'score', mutedRef.current);
          if (mode === 'host' && now - lastPublish > 50) {
            lastPublish = now;
            seq += 1;
            onPublish(toSnapshot(state, seq, Date.now()));
          }
        }
        view = state;
        syncHud({
          scoreL: state.scoreL,
          scoreR: state.scoreR,
          phase: state.phase,
          server: state.server,
          winner: state.winner,
          paused: pausedRef.current,
        });
      } else {
        localY = desiredY(localY);
        if (now - lastPaddleSend > 50) {
          lastPaddleSend = now;
          onPaddle(localY);
        }
        const snap = snapRef.current;
        const shown = snap ? extrapolateSnap(snap, Date.now()) : null;
        const scoreKey = shown
          ? `${shown.scoreL}|${shown.scoreR}|${shown.phase}|${shown.winner}`
          : '';
        if (scoreKey && scoreKey !== lastSound) {
          const prevPhase = lastSound.split('|')[2];
          if (lastSound && shown && lastSound.split('|')[0] !== String(shown.scoreL))
            blip('score', mutedRef.current);
          if (lastSound && shown && lastSound.split('|')[1] !== String(shown.scoreR))
            blip('score', mutedRef.current);
          if (shown?.phase === 'over' && prevPhase !== 'over') blip('win', mutedRef.current);
          lastSound = scoreKey;
        }
        view = shown
          ? {
              ballX: shown.ballX,
              ballY: shown.ballY,
              ballVx: shown.ballVx,
              ballVy: shown.ballVy,
              leftY: shown.leftY,
              rightY: youSide === 'right' ? localY : shown.rightY,
              scoreL: shown.scoreL,
              scoreR: shown.scoreR,
              phase: shown.phase,
              server: shown.server,
              winner: shown.winner,
              serveTimer: 0,
              rally: 0,
            }
          : { ...state, leftY: localY, rightY: localY };
        if (youSide === 'left') view.leftY = localY;
        syncHud({
          scoreL: view.scoreL,
          scoreR: view.scoreR,
          phase: view.phase,
          server: view.server,
          winner: view.winner,
          paused: false,
        });
      }

      hitFlash = Math.max(0, hitFlash - dt * 3.2);
      drawCourt(ctx, canvas, view, hitFlash);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onKeyDown = (event: KeyboardEvent) => {
      const k = event.key.toLowerCase();
      if (k === 'w' || k === 'arrowup') {
        keys.up = true;
        event.preventDefault();
      } else if (k === 's' || k === 'arrowdown') {
        keys.down = true;
        event.preventDefault();
      } else if (k === ' ' && mode === 'solo') {
        event.preventDefault();
        if (event.repeat) return;
        pausedRef.current = !pausedRef.current;
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      const k = event.key.toLowerCase();
      if (k === 'w' || k === 'arrowup') keys.up = false;
      if (k === 's' || k === 'arrowdown') keys.down = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      keys.up = false;
      keys.down = false;
    };
    // The loop reads latest callbacks through the render that mounted this match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode === 'guest') return;
    resetRef.current();
  }, [rematchNonce, mode]);

  const youWon = hud.winner != null && hud.winner === youSide;
  const winnerName = hud.winner === 'left' ? leftName : rightName;

  return (
    <div>
      <DialogHeader>
        <div className="flex items-start justify-between gap-3 pr-8">
          <div>
            <DialogTitle>Ping pong</DialogTitle>
            <DialogDescription>
              First to {WIN_SCORE}, win by {WIN_BY}.{' '}
              {mode === 'solo' ? 'Space pauses.' : 'You each take a side.'}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label={muted ? 'Unmute game sounds' : 'Mute game sounds'}
            aria-pressed={muted}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </DialogHeader>

      <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <ScoreBlock
          name={leftName}
          score={hud.scoreL}
          you={youSide === 'left'}
          serving={hud.phase === 'serve' && hud.server === 'left'}
          align="left"
        />
        <p className="pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
          to {WIN_SCORE}
        </p>
        <ScoreBlock
          name={rightName}
          score={hud.scoreR}
          you={youSide === 'right'}
          serving={hud.phase === 'serve' && hud.server === 'right'}
          align="right"
        />
      </div>
      <p className="sr-only" aria-live="polite">
        {leftName} {hud.scoreL}, {rightName} {hud.scoreR}
        {hud.phase === 'over' ? (youWon ? '. You win.' : `. ${winnerName} wins.`) : ''}
      </p>

      <div className="relative mt-2">
        <canvas
          ref={canvasRef}
          className="h-auto w-full touch-none rounded-md bg-[#0E3B28]"
          style={{ aspectRatio: `${TABLE_W} / ${TABLE_H}` }}
          onPointerDown={(event) => pointAt(event, pointerRef)}
          onPointerMove={(event) => {
            if (event.pointerType === 'mouse' || event.buttons > 0) pointAt(event, pointerRef);
          }}
          onPointerUp={() => {
            pointerRef.current.active = false;
          }}
          onPointerLeave={() => {
            pointerRef.current.active = false;
          }}
        />
        {hud.paused && (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/45">
            <p className="rounded-full bg-white px-4 py-1.5 text-sm font-medium text-foreground">
              Paused
            </p>
          </div>
        )}
        {hud.phase === 'over' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-md bg-black/50 px-4 text-center">
            <p className="text-2xl font-medium text-white">
              {youWon ? 'You win' : `${winnerName} wins`}
            </p>
            <p className="text-sm text-white/80">
              {hud.scoreL} – {hud.scoreR}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  if (mode === 'guest') {
                    setAskedRematch(true);
                    onRematch();
                  } else {
                    resetRef.current();
                  }
                }}
              >
                {askedRematch ? 'Asking for a rematch…' : 'Play again'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="bg-white"
                onClick={onClose}
              >
                Leave table
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HoldButton
            label="Paddle up"
            onHold={(down) => {
              holdRef.current.up = down;
            }}
          >
            Up
          </HoldButton>
          <HoldButton
            label="Paddle down"
            onHold={(down) => {
              holdRef.current.down = down;
            }}
          >
            Down
          </HoldButton>
          <p className="hidden text-xs text-muted-foreground sm:block">
            W and S, arrow keys, or drag on your side of the table.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Leave table
        </Button>
      </div>
    </div>
  );
}

function ScoreBlock({
  name,
  score,
  you,
  serving,
  align,
}: {
  name: string;
  score: number;
  you: boolean;
  serving: boolean;
  align: 'left' | 'right';
}) {
  return (
    <div className={cn('min-w-0', align === 'right' && 'text-right')}>
      <p className="truncate text-xs text-muted-foreground">
        {serving && (
          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[#1F7A45]" aria-hidden />
        )}
        {name}
        {you ? ' (you)' : ''}
      </p>
      <p className="text-3xl font-medium tabular-nums leading-none">{score}</p>
    </div>
  );
}

function HoldButton({
  label,
  onHold,
  children,
}: {
  label: string;
  onHold: (down: boolean) => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="h-9 rounded-lg border border-border bg-secondary px-3 text-xs font-medium text-foreground active:bg-humus/10"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        onHold(true);
      }}
      onPointerUp={() => onHold(false)}
      onPointerCancel={() => onHold(false)}
    >
      {children}
    </button>
  );
}

function pointAt(
  event: PointerEvent<HTMLCanvasElement>,
  pointerRef: MutableRefObject<{ active: boolean; y: number }>,
) {
  const rect = event.currentTarget.getBoundingClientRect();
  const y = ((event.clientY - rect.top) / Math.max(1, rect.height)) * TABLE_H;
  const { min, max } = paddleBounds();
  pointerRef.current.active = true;
  pointerRef.current.y = Math.max(min, Math.min(max, y));
}

function drawCourt(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  state: PongState,
  hitFlash: number,
) {
  ctx.setTransform(canvas.width / TABLE_W, 0, 0, canvas.height / TABLE_H, 0, 0);
  ctx.fillStyle = '#146B45';
  ctx.fillRect(0, 0, TABLE_W, TABLE_H);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 4;
  ctx.strokeRect(8, 8, TABLE_W - 16, TABLE_H - 16);
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 3;
  ctx.setLineDash([10, 12]);
  ctx.beginPath();
  ctx.moveTo(TABLE_W / 2, 16);
  ctx.lineTo(TABLE_W / 2, TABLE_H - 16);
  ctx.stroke();
  ctx.setLineDash([]);

  roundPaddle(ctx, PADDLE_INSET, state.leftY - PADDLE_H / 2, PADDLE_W, PADDLE_H);
  roundPaddle(
    ctx,
    TABLE_W - PADDLE_INSET - PADDLE_W,
    state.rightY - PADDLE_H / 2,
    PADDLE_W,
    PADDLE_H,
  );

  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.arc(state.ballX + 2, state.ballY + 3, BALL_R, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(state.ballX, state.ballY, BALL_R, 0, Math.PI * 2);
  ctx.fill();
  if (hitFlash > 0) {
    ctx.strokeStyle = `rgba(255,255,255,${0.7 * hitFlash})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(state.ballX, state.ballY, BALL_R + 6 + (1 - hitFlash) * 8, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (state.phase === 'serve') {
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = '600 28px Geist, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      state.server === 'left' ? 'Left serves' : 'Right serves',
      TABLE_W / 2,
      TABLE_H / 2 - 36,
    );
  }
}

function roundPaddle(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  const r = w / 2;
  ctx.fillStyle = '#F7F5EF';
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

let audioCtx: AudioContext | null = null;

function blip(kind: 'hit' | 'score' | 'win', muted: boolean) {
  if (muted) return;
  try {
    const Ctx = window.AudioContext;
    audioCtx ??= new Ctx();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const notes = kind === 'hit' ? [660] : kind === 'score' ? [220, 180] : [523, 659, 784];
    notes.forEach((freq, i) => {
      const t = audioCtx!.currentTime + i * 0.09;
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.type = kind === 'hit' ? 'square' : 'triangle';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.035, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + (kind === 'hit' ? 0.06 : 0.16));
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch {
    // Autoplay policies and missing audio APIs just leave the game silent.
  }
}
